/**
 * Phase 1E.2C — My Collection page.
 *
 * Owner-only album management. Every album references an official Phase 1F
 * variant + Phase 1E.2B sticker mode. No completion is stored client-side.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Album, Archive, ArchiveRestore, Copy, Edit3, Eye, EyeOff,
  FolderOpen, Loader2, Plus, Trash2, X, ChevronRight, CheckCircle2,
  Users, Sparkles, Grid, BookOpen, ArrowRight, HelpCircle, Search, Info,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { track, EVENT } from "@/lib/analytics";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { resolveAlbumLabels, resolveAlbumSubtitle } from "@/lib/albumTitle";
import { resolveCollectionTitle, resolveVariantSubtitle, collectionMethodLabelFor } from "@/lib/albumPresentation";
import CollectionMuseum from "@/components/collector/CollectionMuseum";
import {
  buildWcCoverOptions,
  resolveOtherFallbackVariantId,
  wcCoverLabelForVariant,
  OTHER_OPTION_VALUE,
  OTHER_COVER_LABEL,
} from "@/lib/wcCoverLabels";

const METHOD_LABELS = {
  applied_to_album: "Applied to Album",
  stored_in_binder: "Stored in Binder",
  album_only:       "Album Only — Kept Unstickered",
};
const VIS_LABELS = { public: "Public", private: "Private" };
const STATUS_LABELS = {
  in_progress: "In Progress",
  completed:   "Completed",
  archived:    "Archived",
  album_only:  "Album Only",
  sold:        "Sold",
};
// Phase A6 — human-friendly emoji labels used on the MyCollection card
// status badges. Kept separate from STATUS_LABELS so admin surfaces and
// legacy consumers of the plain-text labels stay unchanged.
const STATUS_EMOJI = {
  in_progress: "\u26BD",   // ⚽
  completed:   "\u{1F3C6}", // 🏆
  archived:    "\u{1F4E6}", // 📦
  album_only:  "\u{1F4D6}", // 📖
  sold:        "\u{1F4B0}", // 💰
};
const STATUS_LABELS_EMOJI = {
  in_progress: `${STATUS_EMOJI.in_progress} In Progress`,
  completed:   `${STATUS_EMOJI.completed} Completed`,
  archived:    `${STATUS_EMOJI.archived} Archived`,
  album_only:  `${STATUS_EMOJI.album_only} Album Only`,
  sold:        `${STATUS_EMOJI.sold} Sold`,
};
const STATUS_TONE = {
  in_progress: "bg-yellow-100 text-yellow-900",
  completed:   "bg-emerald-100 text-emerald-900",
  archived:    "bg-neutral-200 text-neutral-700",
  album_only:  "bg-[var(--panini-yellow)] text-[var(--ink)]",
  sold:        "bg-purple-100 text-purple-900",
};
const CONDITION_LABELS = {
  mint: "Mint", near_mint: "Near Mint", excellent: "Excellent",
  very_good: "Very Good", good: "Good", fair: "Fair", poor: "Poor",
};

export default function MyCollection() {
  const nav = useNavigate();
  const { collector } = useCollectorAuth();
  const [albums, setAlbums] = useState([]);
  const [stats, setStats] = useState({ albums: 0, in_progress: 0, completed: 0, archived: 0 });
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [successAlbum, setSuccessAlbum] = useState(null);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Iter Migration — legacy albums awaiting import into the new catalog system.
  const [importQueue, setImportQueue] = useState({ items: [], count: 0, migrated: 0, archived: 0, total_ever: 0 });
  // Session-scoped dismissal — banner reappears on next login until every
  // pending row is either migrated or archived.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const [welcomeSeen, setWelcomeSeen] = useState(() => {
    try { return sessionStorage.getItem("mc-migration-welcome-seen") === "1"; }
    catch { return false; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, q] = await Promise.all([
        api.get("/collector-auth/my-collection/albums",
                { params: { include_archived: showArchived } }),
        api.get("/collector-auth/my-collection/albums/stats"),
        api.get("/collector-auth/my-collection/albums-to-import"),
      ]);
      setAlbums(a.data.items);
      setStats(s.data);
      setImportQueue(q.data || { items: [], count: 0 });
      // First-time welcome — only when there ARE pending imports AND we
      // haven't shown this modal in the current session yet.
      if ((q.data?.count || 0) > 0 && !welcomeSeen) {
        setWelcomeOpen(true);
        setWelcomeSeen(true);
        try { sessionStorage.setItem("mc-migration-welcome-seen", "1"); } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load your collection.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showArchived]);

  const onCreated = (album) => {
    setAddOpen(false);
    setSuccessAlbum(album);
    load();
  };
  const openAlbum = (id) => {
    setSuccessAlbum(null);
    nav(`/dashboard/my-collection/albums/${id}`);
  };

  return (
    <div className="space-y-10 md:space-y-14" data-testid="my-collection">
      <SEO title="My Collection · CollectorUp" />

      {/* Sprint · Profile-first — album-management focus only.
          Collector identity, Founding badge, Future Vision, and the
          Founder closing message now live on My Profile (/dashboard).
          This page is now purely about the albums the collector owns. */}
      <header className="rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--panini-yellow)] p-6 md:p-8 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--panini-red)]">Album Management</div>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">My Collection</h1>
          <p className="mt-2 text-sm text-[var(--smoke)] max-w-xl">
            These are the albums you own — in progress, completed, and collection-only. Manage progress and add new items here.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/dashboard"
            data-testid="mc-view-profile"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-[var(--ink)] hover:bg-neutral-50"
          >
            <BookOpen size={13} /> My Profile
          </Link>
          <button
            data-testid="mc-add-album"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-5 py-2.5 text-xs font-black uppercase tracking-widest shadow-[4px_4px_0_var(--ink)] hover:brightness-110"
          >
            <Plus size={14} /> Add Album
          </button>
        </div>
      </header>

      {/* Iter Migration — Amber banner shown when there are pending legacy
          albums to import. Dismissible for the session only. */}
      {importQueue.count > 0 && !bannerDismissed && (
        <MigrationBanner
          count={importQueue.count}
          onImport={() => setImporterOpen(true)}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* ─── Museum-quality Album Library ─── */}
      {loading ? (
        <div className="p-16 flex items-center justify-center" data-testid="mc-loading">
          <Loader2 className="animate-spin" />
        </div>
      ) : albums.length === 0 ? (
        <EmptyState onAdd={() => setAddOpen(true)} />
      ) : (
        <CollectionMuseum
          albums={albums}
          stats={stats}
          handlers={{
            onOpen: (a) => nav(`/dashboard/my-collection/albums/${a.id}`),
            onEdit: (a) => setEditing(a),
            onDuplicate: (a) => setDuplicating(a),
            onArchive: async (a) => {
              await api.post(`/collector-auth/my-collection/albums/${a.id}/archive`);
              toast.success("Album archived.");
              load();
            },
            onRestore: async (a) => {
              await api.post(`/collector-auth/my-collection/albums/${a.id}/restore`);
              toast.success("Album restored.");
              load();
            },
            onDelete: (a) => setDeleting(a),
            onAdd: () => setAddOpen(true),
          }}
        />
      )}

      {/* Phase A6 — Hide Archived moved here with intentionally reduced
          visual emphasis. Sits below the main grid so power users can
          find it, but never competes with Add Album for attention. */}
      {!loading && (
        <div className="pt-2 flex justify-center" data-testid="mc-archive-strip">
          <button
            data-testid="mc-toggle-archived"
            onClick={() => setShowArchived(v => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)] transition-colors"
          >
            {showArchived ? (
              <><Eye size={12} /> Showing archived</>
            ) : (
              <><EyeOff size={12} /> Show archived albums</>
            )}
          </button>
        </div>
      )}

      {addOpen && (
        <AddAlbumModal
          onClose={() => setAddOpen(false)}
          onCreated={onCreated}
        />
      )}
      {/* Iter Migration — Welcome + Importer modals */}
      {welcomeOpen && (
        <MigrationWelcomeModal
          count={importQueue.count}
          onImport={() => { setWelcomeOpen(false); setImporterOpen(true); }}
          onLater={() => setWelcomeOpen(false)}
        />
      )}
      {importerOpen && (
        <MigrationImporter
          collectorId={collector?.id}
          onClose={() => { setImporterOpen(false); load(); }}
        />
      )}
      {successAlbum && (
        <SuccessModal album={successAlbum}
                      onOpenAlbum={() => openAlbum(successAlbum.id)}
                      onAddAnother={() => { setSuccessAlbum(null); setAddOpen(true); }}
                      onDismiss={() => setSuccessAlbum(null)} />
      )}
      {editing && (
        <EditAlbumModal album={editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
      {duplicating && (
        <DuplicateAlbumModal album={duplicating}
                              onClose={() => setDuplicating(null)}
                              onDone={(dup) => { setDuplicating(null); load(); setSuccessAlbum(dup); }} />
      )}
      {deleting && (
        <DeleteConfirmModal album={deleting}
                              onClose={() => setDeleting(null)}
                              onArchive={async () => {
                                await api.post(`/collector-auth/my-collection/albums/${deleting.id}/archive`);
                                toast.success("Album archived instead of deleted.");
                                setDeleting(null); load();
                              }}
                              onConfirmDelete={async () => {
                                await api.delete(`/collector-auth/my-collection/albums/${deleting.id}`);
                                track(EVENT.ALBUM_DELETED, { album_id: deleting.id });
                                toast.success("Album deleted.");
                                setDeleting(null); load();
                              }} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Stat strip
// ═══════════════════════════════════════════════════════════════════
function StatStrip({ stats }) {
  const cells = [
    { key: "albums",      label: "Albums",       value: stats.albums,      Icon: BookOpen },
    { key: "in_progress", label: "In Progress",  value: stats.in_progress, Icon: Sparkles },
    { key: "completed",   label: "Completed",    value: stats.completed,   Icon: CheckCircle2 },
    { key: "archived",    label: "Archived",     value: stats.archived,    Icon: Archive },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mc-stats">
      {cells.map(c => (
        <div key={c.key} className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4"
             data-testid={`mc-stat-${c.key}`}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--smoke)]">
            <c.Icon size={12} /> {c.label}
          </div>
          <div className="text-3xl font-display mt-1" data-testid={`mc-stat-${c.key}-value`}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Empty state — Phase A6 polish. Future-proof for a collector who
// finishes onboarding without adding any album (skipped picks) OR
// clears their collection later. Never blames the collector.
// ═══════════════════════════════════════════════════════════════════
function EmptyState({ onAdd }) {
  return (
    <div
      className="relative overflow-hidden rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--panini-yellow)]"
      data-testid="mc-empty-state"
    >
      <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-[var(--panini-yellow-soft)]/60" aria-hidden />
      <div className="absolute top-6 right-8 text-4xl select-none opacity-60" aria-hidden>
        {STATUS_EMOJI.completed}
      </div>
      <div className="relative p-8 md:p-12 grid grid-cols-1 md:grid-cols-[auto_1fr] items-center gap-8">
        <div className="mx-auto md:mx-0 h-24 w-24 rounded-2xl bg-[var(--panini-yellow)] border-2 border-[var(--ink)] grid place-items-center shadow-[4px_4px_0_var(--ink)]">
          <Album size={44} className="text-[var(--ink)]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
            Ready when you are
          </div>
          <div className="font-display text-3xl mt-1">Your collection starts here.</div>
          <p className="text-sm text-[var(--smoke)] mt-3 max-w-md">
            Add every album you own — completed, in progress, or hardcover editions
            you keep for the shelf. We&apos;ll track completion for you and help
            you finish what&apos;s missing.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={onAdd}
              data-testid="mc-empty-add"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-6 py-3 text-sm font-black uppercase tracking-widest shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5 transition-transform"
            >
              <Plus size={18} /> Add your first album
            </button>
            <Link
              to="/dashboard/certificates"
              data-testid="mc-empty-my-certificates"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-neutral-50"
            >
              <BookOpen size={14} /> My Certificates
            </Link>
          </div>
          <div className="mt-5 text-[11px] text-[var(--smoke)] flex items-center gap-2">
            <Info size={12} />
            Tip: mark completed editions so we can celebrate them on your public profile.
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Album card
// ═══════════════════════════════════════════════════════════════════
function AlbumCard({ album, onOpen, onEdit, onDuplicate, onArchive, onRestore, onDelete }) {
  const cat = album.catalog || {};
  const labels = resolveAlbumLabels(album, cat);
  // Phase A · Album Title Hierarchy — the primary card title is now the
  // canonical collection name (from `catalog_releases.official_title`)
  // and the secondary line is the variant descriptor "USA · Hard Cover".
  // The old `labels.title` (variant name) is retained only as the third
  // fallback for the very rare case where the release row has no
  // official_title populated.
  const collectionTitle = resolveCollectionTitle(album) || labels.title;
  const variantSubtitle = resolveVariantSubtitle(album, cat);
  // Iter Catalog-Cover-Meta — canonical cover resolver reads the resolved
  // `cover_image_url` published by the backend. Falls back to the catalog
  // variant/release `cover_image_url` alias so the card still renders even
  // if the backend hasn't populated the top-level field for some reason.
  const cover = album.cover_image_url
             || cat.album_variant?.cover_image_url
             || cat.release?.cover_image_url;
  const isArchived = album.status === "archived";
  const comp = album.completion || {};
  const isAlbumOnly = album.is_album_only || album.collection_method === "album_only";
  const pct = Number(album.completion_percentage ?? comp.completion_percentage ?? 0);
  const barColor = pct >= 100 ? "bg-emerald-500"
                : pct >= 95   ? "bg-emerald-400"
                : pct >= 50   ? "bg-[var(--panini-yellow)]"
                : pct > 0     ? "bg-orange-400"
                : "bg-neutral-300";
  return (
    <article className={"border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden " +
                        "shadow-[4px_4px_0_var(--ink)] flex flex-col " +
                        (isArchived ? "opacity-75" : "")}
             data-testid={`mc-card-${album.id}`}>
      {/* Iter QA #005 refinement — status badge sits ABOVE the cover so it
          reads as a standalone label rather than an overlay. */}
      <div className="px-4 pt-4 flex items-center justify-between gap-2">
        {/* Phase A6 — status badge now uses the emoji labels
            (🏆 Completed / ⚽ In Progress etc.) for at-a-glance
            recognition. STATUS_TONE colours are preserved. */}
        <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-widest font-bold " + STATUS_TONE[album.status]}
              data-testid={`mc-card-status-${album.id}`}>
          {STATUS_LABELS_EMOJI[album.status] || STATUS_LABELS[album.status]}
        </span>
        {!isAlbumOnly && (comp.is_complete || comp.is_near_completion) && (
          <span className={"px-2 py-1 rounded-full text-[9px] uppercase tracking-widest font-bold " +
            (comp.is_complete ? "bg-emerald-600 text-white"
                              : "bg-emerald-100 text-emerald-900 border border-emerald-500")}
                data-testid={`mc-card-badge-${album.id}`}>
            {comp.is_complete ? "Completed" : "Near completion"}
          </span>
        )}
      </div>

      {/* Iter QA #005 refinement — portrait 3:4 frame so covers read as
          physical albums rather than banners. contain-fit + neutral bg
          rules from the initial fix stay in place. */}
      <div className="mx-4 mt-3 aspect-[3/4] bg-neutral-50 relative overflow-hidden flex items-center justify-center rounded-lg border border-[var(--line)]">
        {cover ? (
          <img src={cover} alt=""
               className="max-w-full max-h-full w-auto h-auto object-contain object-center"
               data-testid={`mc-card-cover-${album.id}`} />
        ) : (
          <div className="w-full h-full grid place-items-center text-[var(--smoke)]">
            <Album size={48} />
          </div>
        )}
        {isAlbumOnly && (
          <div className="absolute bottom-2 left-2">
            <span className="px-2 py-1 rounded-full text-[9px] uppercase tracking-widest font-bold bg-[var(--ink)] text-[var(--panini-yellow)]"
                  data-testid={`mc-card-album-only-badge-${album.id}`}>
              Album Only
            </span>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {/* Phase A · Primary line — canonical collection title (large,
            bold). Allowed to wrap to two lines on mobile; never
            truncated with an ellipsis. */}
        <div className="font-display text-lg leading-tight font-bold break-words"
             data-testid={`mc-card-title-${album.id}`}>
          {collectionTitle}
        </div>
        {/* Phase A · Secondary line — variant descriptor "USA · Hard Cover" */}
        {variantSubtitle && (
          <div className="text-xs text-[var(--smoke)] mt-1"
               data-testid={`mc-card-subtitle-${album.id}`}>
            {variantSubtitle}
          </div>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-3">
          {collectionMethodLabelFor(album) && (
            <Meta k="Method" v={collectionMethodLabelFor(album)} />
          )}
          {!isAlbumOnly && <Meta k="Sticker mode" v={cat.sticker_mode?.name || "—"} />}
          <Meta k="Visibility" v={VIS_LABELS[album.visibility]} />
          <Meta k="Updated" v={fmtDate(album.updated_at)} />
        </dl>

        {/* Progress bar + counts — hidden entirely for ALBUM_ONLY method */}
        {isAlbumOnly ? (
          <div className="mt-3 rounded-lg border-2 border-dashed border-[var(--ink)] bg-[var(--panini-yellow-soft)]/40 px-3 py-3 text-xs"
               data-testid={`mc-card-album-only-note-${album.id}`}>
            <div className="font-bold text-[var(--ink)]">Kept in mint condition — no sticker tracking.</div>
            <div className="mt-1 text-[var(--smoke)]">
              You&apos;re keeping this album untouched. Completion progress and sticker
              inventory are intentionally hidden.
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Completion</div>
              <div className="text-2xl font-display" data-testid={`mc-card-completion-${album.id}`}>
                {pct}%
              </div>
            </div>
            <div className="h-2 mt-1 bg-neutral-200 rounded-full overflow-hidden"
                 role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                 data-testid={`mc-card-bar-${album.id}`}>
              <div className={"h-full transition-[width] " + barColor}
                   style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
            </div>
            <div className="grid grid-cols-4 gap-1 mt-2 text-[10px] uppercase tracking-widest text-center">
              <MiniCount label="Collected" value={comp.owned} testid={`mc-card-owned-${album.id}`} tone="OWNED" />
              <MiniCount label="Missing" value={comp.missing} testid={`mc-card-missing-${album.id}`} tone="MISSING" />
              <MiniCount label="Duplicate" value={comp.duplicate} testid={`mc-card-duplicate-${album.id}`} tone="DUPLICATE" />
              <MiniCount label="Remaining" value={comp.remaining} testid={`mc-card-remaining-${album.id}`} tone="UNKNOWN" />
            </div>
          </div>
        )}

        {/* Iter QA #005 refinement — optional nickname sits last in the body,
            per the collector-card visual hierarchy spec. Suppressed for
            empty / generic / echoed values. */}
        {labels.subtitle && (
          <div className="text-sm italic text-[var(--smoke)] mt-3"
               data-testid={`mc-card-nickname-${album.id}`}>
            &ldquo;{labels.subtitle}&rdquo;
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-neutral-200 flex flex-wrap gap-2">
          <IconBtn testid={`mc-card-open-${album.id}`} title="Open album"
                   onClick={onOpen}
                   Icon={FolderOpen} label="Open" />
          <IconBtn testid={`mc-card-edit-${album.id}`} onClick={onEdit} Icon={Edit3} label="Edit" />
          <IconBtn testid={`mc-card-duplicate-${album.id}`} onClick={onDuplicate} Icon={Copy} label="Duplicate" />
          {isArchived ? (
            <IconBtn testid={`mc-card-restore-${album.id}`} onClick={onRestore}
                     Icon={ArchiveRestore} label="Restore" />
          ) : (
            <IconBtn testid={`mc-card-archive-${album.id}`} onClick={onArchive}
                     Icon={Archive} label="Archive" />
          )}
          <IconBtn testid={`mc-card-delete-${album.id}`} onClick={onDelete}
                   Icon={Trash2} label="Delete" tone="danger" />
        </div>
      </div>
    </article>
  );
}

const MC_TONE = {
  OWNED:     "text-emerald-800 bg-emerald-50 border-emerald-200",
  MISSING:   "text-red-800     bg-red-50     border-red-200",
  DUPLICATE: "text-blue-800    bg-blue-50    border-blue-200",
  UNKNOWN:   "text-neutral-700 bg-neutral-100 border-neutral-200",
};
function MiniCount({ label, value, tone, testid }) {
  return (
    <div className={"border rounded px-1 py-0.5 " + (MC_TONE[tone] || MC_TONE.UNKNOWN)}
         data-testid={testid}>
      <div className="font-mono font-bold text-sm">{value ?? 0}</div>
      <div className="text-[9px]">{label}</div>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{k}</dt>
      <dd className="font-medium">{v || "—"}</dd>
    </>
  );
}

function IconBtn({ testid, onClick, Icon, label, tone, disabled, title }) {
  const cls = tone === "danger"
    ? "border-red-300 text-red-800 hover:bg-red-50"
    : "border-neutral-300 text-[var(--ink)] hover:bg-neutral-50";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} title={title || label}
            className={"inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest " +
                       "px-2 py-1 rounded-full border-2 " + cls +
                       (disabled ? " opacity-50 cursor-not-allowed" : "")}>
      <Icon size={12} /> {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Add Album Modal — cascading picker
// ═══════════════════════════════════════════════════════════════════

// Phase 3 · The one family code the wizard treats specially. FIFA World
// Cup is pinned as the primary option on the family picker AND the
// wizard's Variant step is bypassed for WC 2026 (every cover variant
// resolves to the same `panini-wc-2026-canonical` checklist, so the
// choice is metadata-only and is collected on the Details step via the
// "Album Type / Cover Design" dropdown).
const FIFA_WC_FAMILY_CODE = "PANINI-FIFA-WC";
const FIFA_WC_FAMILY_SLUG = "fifa-world-cup";
const WC_2026_RELEASE_SLUG = "wc26";

function isFifaWorldCupFamily(fam) {
  if (!fam) return false;
  return fam.code === FIFA_WC_FAMILY_CODE || fam.slug === FIFA_WC_FAMILY_SLUG;
}

function isWorldCup2026Release(rel) {
  if (!rel) return false;
  return rel.slug === WC_2026_RELEASE_SLUG || rel.year === 2026;
}

function AddAlbumModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [manufacturers, setManufacturers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [releases, setReleases] = useState([]);
  const [variants, setVariants] = useState([]);
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);

  const [selM, setSelM] = useState(null);
  const [selF, setSelF] = useState(null);
  const [selR, setSelR] = useState(null);
  const [selV, setSelV] = useState(null);
  const [modeId, setModeId] = useState("");
  const [nickname, setNickname] = useState("");
  const [method, setMethod] = useState("applied_to_album");
  const [visibility, setVisibility] = useState("public");
  const [status, setStatus] = useState("in_progress");
  // Iter Album Only — dedicated condition selector (mint/near_mint/…).
  // Only surfaced in the UI when method='album_only'; ignored otherwise.
  const [albumCondition, setAlbumCondition] = useState("mint");
  // Iter Cover-Label — flags when the WC-2026 collector picked
  // "Other" from the customer-facing cover dropdown. When true, we
  // submit the Softcover fallback variant + `cover_label_override:
  // "Other"` on album create.
  const [wcCoverIsOther, setWcCoverIsOther] = useState(false);

  useEffect(() => {
    (async () => {
      const [m, sm] = await Promise.all([
        api.get("/catalog/manufacturers"),
        api.get("/catalog/sticker-modes"),
      ]);
      setManufacturers(m.data.items || []);
      const items = sm.data.items || [];
      setModes(items);
      // Default to MIXED per spec
      const mixed = items.find(x => x.code === "MIXED") || items[0];
      if (mixed) setModeId(mixed.id);
    })();
  }, []);

  const chooseManufacturer = async (mfr) => {
    setSelM(mfr); setSelF(null); setSelR(null); setSelV(null);
    const r = await api.get(`/catalog/manufacturers/${mfr.slug}/families`);
    setFamilies(r.data.items || []);
    setStep(2);
  };
  const chooseFamily = async (fam) => {
    setSelF(fam); setSelR(null); setSelV(null);
    const r = await api.get(`/catalog/families/${fam.slug}/releases`);
    setReleases(r.data.items || []);
    setStep(3);
  };
  const chooseRelease = async (rel) => {
    setSelR(rel); setSelV(null);
    const r = await api.get(`/catalog/releases/${rel.slug}/variants`);
    const items = r.data.items || [];
    setVariants(items);
    if (items.length === 0) {
      toast.info(`${rel.name}${rel.host_country ? ` · ${rel.host_country}` : ""} — variants coming soon.`);
      return; // stay on step 3
    }
    // Phase 3 · World Cup 2026 bypass — every cover design resolves to
    // the same `panini-wc-2026-canonical` checklist, so we skip the
    // variant wizard step entirely and let the collector choose their
    // cover from the "Album Type / Cover Design" dropdown on the
    // Details step (below Nickname, above Collection Method).
    if (isFifaWorldCupFamily(selF) && isWorldCup2026Release(rel)) {
      setSelV(items[0]);   // pre-select first — user can override on Details
      setStep(5);
      return;
    }
    setStep(4);
  };
  const chooseVariant = (v) => {
    setSelV(v);
    setStep(5);
  };

  // Phase A · Wizard step counter is now a STABLE five-step flow. The
  // wizard always advertises "Step N of 5" — even when the internal
  // variant-picker step is skipped for WC 2026 (the collector still
  // sees Step 1, 2, 3, then Step 5 for the details form). Phase B
  // will introduce the dedicated Step 4 (Album Format & Cover Design)
  // view; for now the numbering never collapses to /4.
  const wcSkipsVariantStep = isFifaWorldCupFamily(selF) && isWorldCup2026Release(selR);
  const totalSteps = 5;
  const displayedStep = Math.min(step, totalSteps);

  const submit = async () => {
    setBusy(true);
    try {
      const isAlbumOnly = method === "album_only";
      // Iter Cover-Label — persist the customer-facing label the
      // collector actually saw in the dropdown. For WC 2026 the
      // dropdown remaps 8 catalog rows (e.g. "Switzerland Hardcover"
      // → "Hardcover - United Edition") and adds an "Other" option
      // that submits under the Softcover fallback variant.
      let coverLabelOverride = null;
      if (wcSkipsVariantStep) {
        if (wcCoverIsOther) {
          coverLabelOverride = OTHER_COVER_LABEL;
        } else if (selV) {
          const entry = buildWcCoverOptions(variants).find(
            ([val]) => val === selV.id
          );
          const label = entry ? entry[1] : null;
          // Only persist the override when it actually differs from
          // the raw catalog label — keeps the DB clean.
          const catalogLabel = selV.public_display_name || selV.name;
          if (label && label !== catalogLabel) coverLabelOverride = label;
        }
      }
      const payload = {
        album_variant_id: selV.id,
        collection_method: method,
        visibility,
        nickname: nickname.trim() || null,
        ...(coverLabelOverride ? { cover_label_override: coverLabelOverride } : {}),
        // Server coerces status/sticker_mode for album_only automatically,
        // but we send explicit values for standard albums.
        ...(isAlbumOnly
              ? { status: "album_only", album_condition: albumCondition }
              : { status, sticker_mode_id: modeId }),
      };
      const r = await api.post("/collector-auth/my-collection/albums", payload);
      // Spec-name (album_added) — variant_id kept as an anonymous
      // catalog key (not a customer identifier). Album year / cover
      // type derived from the selected variant.
      track(EVENT.ALBUM_ADDED, {
        album_year:        selV?.year || undefined,
        cover_type:        selV?.cover_type || selV?.cover || undefined,
        collection_method: method,
      }, { once: true, key: `album-${r.data?.id || selV.id}` });
      toast.success("Album added to your collection.");
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create album.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="mc-add-modal" size="lg">
      <ModalHeader onClose={onClose} title="Add Album" subtitle={`Step ${displayedStep} of ${totalSteps}`} />

      {/* Breadcrumb — hides the Variant crumb entirely for the WC-2026
          flow since the wizard skips that step. */}
      <div className="px-6 py-3 border-b border-neutral-200 bg-neutral-50 text-xs flex flex-wrap gap-1 items-center">
        <Crumb label="Manufacturer" val={selM?.name} onClick={() => setStep(1)} active={step===1} />
        <ChevronRight size={12} />
        <Crumb label="Family" val={selF?.name} onClick={() => selM && setStep(2)} active={step===2} />
        <ChevronRight size={12} />
        <Crumb label="Release" val={selR?.name} onClick={() => selF && setStep(3)} active={step===3} />
        {!wcSkipsVariantStep && (
          <>
            <ChevronRight size={12} />
            <Crumb label="Variant" val={selV?.name} onClick={() => selR && setStep(4)} active={step===4} />
          </>
        )}
        <ChevronRight size={12} />
        <Crumb label="Details" val={step===5?"":""} onClick={() => selV && setStep(5)} active={step===5} />
      </div>

      <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
        {step === 1 && (
          <PickerGrid items={manufacturers} keyProp="id" onPick={chooseManufacturer}
                      testid="mc-picker-manufacturer" />
        )}
        {step === 2 && (
          <FamilyPickerGrid items={families} onPick={chooseFamily}
                            testid="mc-picker-family" />
        )}
        {step === 3 && (
          <PickerGrid items={releases} keyProp="id" onPick={chooseRelease}
                      subLabel={(r) => {
                        const y = r.year ? String(r.year) : "";
                        const h = r.host_country || "";
                        return y && h ? `${y} · ${h}` : (y || h);
                      }}
                      testid="mc-picker-release" />
        )}
        {step === 4 && (
          <PickerGrid items={variants} keyProp="id" onPick={chooseVariant}
                      subLabel={(v) => v.country_code || v.format || ""}
                      testid="mc-picker-variant" />
        )}

        {step === 5 && selV && (
          <div className="space-y-4" data-testid="mc-add-details">
            <SummaryLine title="Selected album"
                          value={(() => {
                            // Iter Final Pre-deploy — the summary line
                            // must always show the customer-facing
                            // label the collector actually saw in the
                            // dropdown (e.g. "Hardcover - United
                            // Edition"), never the raw catalog name
                            // ("Switzerland Hardcover"). For the WC-
                            // 2026 flow we resolve the label from the
                            // wcCoverLabels map (with "Other" winning
                            // over the fallback variant).
                            let coverLabel = selV.public_display_name || selV.name;
                            if (wcSkipsVariantStep) {
                              if (wcCoverIsOther) {
                                coverLabel = OTHER_COVER_LABEL;
                              } else {
                                coverLabel = wcCoverLabelForVariant(selV) || coverLabel;
                              }
                            }
                            return `${selM.name} · ${selR.name} · ${coverLabel}`;
                          })()}
                          data-testid="mc-form-summary" />
            <Field label="Nickname (optional)" value={nickname} onChange={setNickname}
                   maxLength={80} testid="mc-form-nickname"
                   placeholder="Leave blank to just show the catalog name"
                   hint="Only shown when it differs from the catalog title — e.g. “Factory Sealed” or “Kid’s copy”." />
            {/* Phase 3 · Album Type / Cover Design dropdown — surfaced
                ONLY for the WC-2026 flow where the wizard skipped its
                variant step. All 2026 covers resolve to the same
                `panini-wc-2026-canonical` checklist, so this is pure
                metadata: changing it never touches sticker codes,
                progress, missing data, duplicates, or matching data. */}
            {wcSkipsVariantStep && variants.length > 0 && (
              <SelectField label="Album Type / Cover Design"
                           value={wcCoverIsOther ? OTHER_OPTION_VALUE : (selV?.id || "")}
                           onChange={(next) => {
                             if (next === OTHER_OPTION_VALUE) {
                               // Route "Other" to the Softcover
                               // fallback so the album still resolves
                               // to the canonical 992-position
                               // checklist. The customer-facing label
                               // is preserved via cover_label_override
                               // on submit.
                               const fallbackId = resolveOtherFallbackVariantId(variants);
                               const fallback = variants.find(v => v.id === fallbackId) || variants[0];
                               if (fallback) setSelV(fallback);
                               setWcCoverIsOther(true);
                               return;
                             }
                             const v = variants.find(x => x.id === next);
                             if (v) {
                               setSelV(v);
                               setWcCoverIsOther(false);
                             }
                           }}
                           options={buildWcCoverOptions(variants)}
                           hint="Every 2026 cover uses the same 992-position canonical checklist — this is the label that will appear on your profile card."
                           testid="mc-form-wc-cover-design" />
            )}
            <SelectField label="Collection Method" value={method} onChange={setMethod}
                         options={Object.entries(METHOD_LABELS)}
                         hint={method === "album_only"
                                ? "Album Only keeps this album unstickered — no sticker inventory or completion tracking."
                                : "Applied albums track completion; Binder stores stickers separately."}
                         testid="mc-form-method" />
            {method !== "album_only" && (
              <SelectField label="Sticker Mode" value={modeId} onChange={setModeId}
                           options={modes.map(m => [m.id, m.name])}
                           testid="mc-form-sticker-mode" />
            )}
            <SelectField label="Visibility" value={visibility} onChange={setVisibility}
                         options={[["public","Public (default)"], ["private","Private (only you)"]]}
                         hint="New albums default to Public. You can always flip this later."
                         testid="mc-form-visibility" />
            {method === "album_only" ? (
              <SelectField label="Album Condition" value={albumCondition} onChange={setAlbumCondition}
                            options={[
                              ["mint", "Mint"],
                              ["near_mint", "Near Mint"],
                              ["excellent", "Excellent"],
                              ["very_good", "Very Good"],
                              ["good", "Good"],
                              ["fair", "Fair"],
                              ["poor", "Poor"],
                            ]}
                            hint="Track the physical condition of your sealed album."
                            testid="mc-form-condition" />
            ) : (
              <SelectField label="Status" value={status} onChange={setStatus}
                            options={[["in_progress", STATUS_LABELS.in_progress],
                                      ["completed",   STATUS_LABELS.completed]]}
                            testid="mc-form-status" />
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between">
        <div className="text-xs text-[var(--smoke)]">
          {step === 5
            ? "You can rename, change visibility, or archive this album any time."
            : "Pick your album step by step from the official catalog."}
        </div>
        {step === 5 ? (
          <button className="btn-primary flex items-center gap-2"
                  disabled={busy || (method !== "album_only" && !modeId)} onClick={submit}
                  data-testid="mc-form-create">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
            Create Album
          </button>
        ) : (
          <div className="text-xs text-[var(--smoke)]">
            {step === 1 && manufacturers.length === 0 && <em>Loading manufacturers…</em>}
          </div>
        )}
      </div>
    </Modal>
  );
}

function PickerGrid({ items, keyProp, onPick, subLabel, testid }) {
  if (!items.length) return <div className="text-sm text-[var(--smoke)]">Loading…</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testid}>
      {items.map(it => (
        <button key={it[keyProp]}
                onClick={() => onPick(it)}
                data-testid={`${testid}-item-${it.slug || it.code || it.id}`}
                className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 text-left hover:bg-[var(--panini-yellow-soft)] transition">
          <div className="font-display text-lg">{it.name}</div>
          <div className="text-xs font-mono text-[var(--smoke)] mt-1">
            {it.code || it.slug}{subLabel ? ` · ${subLabel(it) || ""}` : ""}
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * Phase 3 · Family picker with FIFA World Cup pinned at the top center
 * as the primary option. The remaining families keep the existing
 * side-by-side two-column layout below. Nothing is removed.
 */
function FamilyPickerGrid({ items, onPick, testid }) {
  if (!items.length) return <div className="text-sm text-[var(--smoke)]">Loading…</div>;
  const primary = items.find(isFifaWorldCupFamily) || null;
  const rest = primary ? items.filter(f => f !== primary) : items;
  return (
    <div className="space-y-4" data-testid={testid}>
      {primary && (
        <div className="flex justify-center">
          <button
            onClick={() => onPick(primary)}
            data-testid={`${testid}-primary-${primary.slug || primary.code || primary.id}`}
            className="w-full max-w-md border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-red)] text-white p-6 text-center shadow-[4px_4px_0_var(--ink)] hover:brightness-110 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--panini-yellow)] mb-1">
              Featured
            </div>
            <div className="font-display text-2xl md:text-3xl">{primary.name}</div>
            <div className="text-xs font-mono text-white/80 mt-2">
              {primary.code || primary.slug}
            </div>
          </button>
        </div>
      )}
      {rest.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"
             data-testid={`${testid}-rest`}>
          {rest.map(it => (
            <button key={it.id}
                    onClick={() => onPick(it)}
                    data-testid={`${testid}-item-${it.slug || it.code || it.id}`}
                    className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 text-left hover:bg-[var(--panini-yellow-soft)] transition">
              <div className="font-display text-lg">{it.name}</div>
              <div className="text-xs font-mono text-[var(--smoke)] mt-1">
                {it.code || it.slug}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Crumb({ label, val, onClick, active }) {
  return (
    <button onClick={onClick} className={"px-2 py-1 rounded " + (active ? "bg-[var(--ink)] text-white" : "hover:bg-neutral-200")}>
      <span className="uppercase tracking-widest text-[9px]">{label}</span>
      {val ? <span className="ml-1 text-xs">· {val}</span> : null}
    </button>
  );
}

function SummaryLine({ title, value, "data-testid": testid }) {
  return (
    <div className="border border-emerald-300 bg-emerald-50 rounded-xl p-3 text-sm" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-emerald-900 font-bold">{title}</div>
      <div className="text-emerald-950 font-medium">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Success modal
// ═══════════════════════════════════════════════════════════════════
function SuccessModal({ album, onOpenAlbum, onAddAnother, onDismiss }) {
  const cat = album.catalog || {};
  return (
    <Modal onClose={onDismiss} testid="mc-success-modal" size="md">
      <div className="p-8 text-center" data-testid="mc-success-body">
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-4">
          <CheckCircle2 size={28} className="text-emerald-700" />
        </div>
        <div className="font-display text-2xl">Album Added 🎉</div>
        <div className="text-sm text-[var(--smoke)] mt-2">
          {cat.manufacturer?.name} · {cat.release?.name}
        </div>
        <div className="font-bold mt-1">{cat.album_variant?.name}</div>
        {(() => {
          const sub = resolveAlbumSubtitle(album.nickname, cat.album_variant?.name);
          return sub ? <div className="italic text-sm text-[var(--smoke)]">&ldquo;{sub}&rdquo;</div> : null;
        })()}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Completion</div>
          <div className="text-4xl font-display">0%</div>
        </div>
        <div className="text-xs text-[var(--smoke)] mt-4">
          Ready to import your missing stickers.
        </div>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <button className="btn-primary" onClick={onOpenAlbum} data-testid="mc-success-open">
            Open Album
          </button>
          <button className="btn-secondary" onClick={onAddAnother} data-testid="mc-success-add-another">
            Add Another Album
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Edit modal
// ═══════════════════════════════════════════════════════════════════
function EditAlbumModal({ album, onClose, onSaved }) {
  const [nickname, setNickname] = useState(album.nickname || "");
  const [visibility, setVisibility] = useState(album.visibility);
  const [method, setMethod] = useState(album.collection_method);
  const [modeId, setModeId] = useState(album.sticker_mode_id);
  const [status, setStatus] = useState(
    (album.status === "archived" || album.status === "album_only") ? "in_progress" : album.status
  );
  const [albumCondition, setAlbumCondition] = useState(album.album_condition || "mint");
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const isAlbumOnly = method === "album_only";

  useEffect(() => {
    api.get("/catalog/sticker-modes").then(r => setModes(r.data.items || []));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        nickname: nickname.trim() || null,
        visibility,
        collection_method: method,
        ...(isAlbumOnly
              ? { album_condition: albumCondition }
              : { status, sticker_mode_id: modeId }),
      };
      await api.patch(`/collector-auth/my-collection/albums/${album.id}`, payload);
      if (!isAlbumOnly && status === "completed" && album.status !== "completed") {
        track(EVENT.ALBUM_COMPLETED, { album_id: album.id });
      }
      toast.success("Album updated.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="mc-edit-modal" size="md">
      <ModalHeader onClose={onClose} title="Edit Album"
                    subtitle="Identity (manufacturer / release / variant) is locked." />
      <div className="p-6 space-y-4">
        <Field label="Nickname (optional)" value={nickname} onChange={setNickname}
               maxLength={80} testid="mc-edit-nickname"
               placeholder="Leave blank to just show the catalog name"
               hint="Only shown when it differs from the catalog title." />
        <SelectField label="Collection Method" value={method} onChange={setMethod}
                     options={Object.entries(METHOD_LABELS)}
                     hint={isAlbumOnly ? "Album Only hides sticker inventory and completion tracking." : ""}
                     testid="mc-edit-method" />
        {!isAlbumOnly && (
          <SelectField label="Sticker Mode" value={modeId} onChange={setModeId}
                        options={modes.map(m => [m.id, m.name])}
                        testid="mc-edit-mode" />
        )}
        <SelectField label="Visibility" value={visibility} onChange={setVisibility}
                     options={[["private","Private"], ["public","Public"]]}
                     testid="mc-edit-visibility" />
        {isAlbumOnly ? (
          <SelectField label="Album Condition" value={albumCondition} onChange={setAlbumCondition}
                        options={Object.entries(CONDITION_LABELS)}
                        testid="mc-edit-condition" />
        ) : (
          <SelectField label="Status" value={status} onChange={setStatus}
                        options={[["in_progress", STATUS_LABELS.in_progress],
                                  ["completed",   STATUS_LABELS.completed]]}
                        testid="mc-edit-status" />
        )}
      </div>
      <div className="p-4 border-t bg-neutral-50 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save}
                data-testid="mc-edit-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Save changes"}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Duplicate modal
// ═══════════════════════════════════════════════════════════════════
function DuplicateAlbumModal({ album, onClose, onDone }) {
  const [nickname, setNickname] = useState("");
  const [busy, setBusy] = useState(false);
  const cat = album.catalog || {};

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/collector-auth/my-collection/albums/${album.id}/duplicate`,
                                { nickname: nickname.trim() || null });
      toast.success("Album duplicated.");
      onDone(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Duplicate failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="mc-dup-modal" size="md">
      <ModalHeader onClose={onClose} title="Duplicate Album"
                    subtitle="Creates a fresh copy — completion and stats reset." />
      <div className="p-6 space-y-4">
        <div className="border border-neutral-300 rounded-lg p-3 bg-neutral-50 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Source</div>
          <div className="font-bold">{cat.manufacturer?.name} · {cat.release?.name}</div>
          <div>{cat.album_variant?.name}</div>
        </div>
        <Field label="New nickname" value={nickname} onChange={setNickname} maxLength={80}
               placeholder="e.g. Backup, Kid's copy…" testid="mc-dup-nickname" />
      </div>
      <div className="p-4 border-t bg-neutral-50 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={submit}
                data-testid="mc-dup-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Duplicate"}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Delete confirm
// ═══════════════════════════════════════════════════════════════════
function DeleteConfirmModal({ album, onClose, onArchive, onConfirmDelete }) {
  const [busy, setBusy] = useState(false);
  const wrap = (fn) => async () => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <Modal onClose={onClose} testid="mc-delete-modal" size="md">
      <ModalHeader onClose={onClose} title="Delete Album" tone="danger" />
      <div className="p-6 space-y-3 text-sm">
        <p>
          Deleting this album permanently removes its collection data. This cannot be undone.
        </p>
        <p className="text-[var(--smoke)]">
          Prefer <b>Archive</b> instead — archived albums stay in your library and can be restored anytime.
        </p>
      </div>
      <div className="p-4 border-t bg-neutral-50 flex flex-wrap justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="border-2 border-[var(--ink)] rounded-full px-4 py-2 text-sm font-bold bg-white hover:bg-neutral-100"
                onClick={wrap(onArchive)} data-testid="mc-delete-archive-instead">
          <Archive size={14} className="inline mr-1" /> Archive instead
        </button>
        <button className="rounded-full px-4 py-2 text-sm font-bold bg-red-700 text-white hover:bg-red-800"
                disabled={busy} onClick={wrap(onConfirmDelete)}
                data-testid="mc-delete-confirm">
          {busy ? <Loader2 size={14} className="inline animate-spin" /> : <Trash2 size={14} className="inline mr-1" />}
          Delete permanently
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Building blocks
// ═══════════════════════════════════════════════════════════════════
function Modal({ children, onClose, testid, size }) {
  const w = size === "lg" ? "max-w-3xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
         role="dialog" aria-modal="true" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={"relative bg-white border-2 border-[var(--ink)] shadow-2xl w-full " + w +
                      " rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ onClose, title, subtitle, tone }) {
  const bg = tone === "danger" ? "bg-red-50 border-red-300" : "bg-[var(--panini-yellow-soft)]";
  return (
    <div className={"px-6 py-4 flex items-start justify-between gap-3 border-b " + bg}>
      <div>
        <div className="font-display text-xl">{title}</div>
        {subtitle && <div className="text-xs text-[var(--smoke)] mt-1">{subtitle}</div>}
      </div>
      <button onClick={onClose} className="p-1 rounded hover:bg-white/60" aria-label="Close">
        <X size={18} />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", maxLength, placeholder, hint }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">{label}</span>
      <input type={type} value={value ?? ""} maxLength={maxLength}
             onChange={e => onChange(e.target.value)}
             placeholder={placeholder}
             className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
             data-testid={testid} />
      {hint && <span className="text-[10px] text-[var(--smoke)] mt-1">{hint}</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid, hint }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">{label}</span>
      <select value={value ?? ""} onChange={e => onChange(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
              data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {hint && <span className="text-[10px] text-[var(--smoke)] mt-1">{hint}</span>}
    </label>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}


// ─────────────────────────────────────────────────────────────────
// Iter Migration · Banner + Welcome + Importer
// ─────────────────────────────────────────────────────────────────
function MigrationBanner({ count, onImport, onDismiss }) {
  return (
    <div className="rounded-2xl border-2 border-amber-700 bg-amber-50 px-4 py-3 md:px-5 md:py-4 flex items-start gap-3"
         data-testid="mc-migration-banner">
      <div className="mt-0.5 text-amber-800"><Info size={20} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg md:text-xl text-amber-950">Albums to import</div>
        <div className="text-sm text-amber-900 mt-0.5">
          You have <span className="font-bold">{count} album{count === 1 ? "" : "s"}</span> from your previous CollectorUp
          library ready to import into the catalog system.
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onImport} className="btn-primary text-xs flex items-center gap-1" data-testid="mc-migration-banner-import">
          Import albums <ArrowRight size={12} />
        </button>
        <button onClick={onDismiss} aria-label="Dismiss for now"
                className="p-2 rounded-full hover:bg-amber-100 text-amber-900" data-testid="mc-migration-banner-dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}


function MigrationWelcomeModal({ count, onImport, onLater }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" role="dialog"
         data-testid="mc-migration-welcome">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-yellow)] max-w-lg w-full">
        <div className="px-6 py-5 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">Collection upgrade</div>
          <div className="font-display text-2xl md:text-3xl mt-0.5">Welcome to the new CollectorUp Collection System</div>
        </div>
        <div className="px-6 py-6 text-sm space-y-3">
          <p>We&apos;ve upgraded how albums are managed. Every album now lives in the official catalog with live per-sticker tracking, smart matches, and public profiles.</p>
          <p>We found <span className="font-bold">{count} existing album{count === 1 ? "" : "s"}</span> ready to be imported into your new collection.</p>
          <p className="text-xs text-[var(--smoke)]">
            Import brings over your album title, status, notes, and creation date. Sticker inventory wasn&apos;t
            tracked in the old system — you&apos;ll add that after import to see live progress.
          </p>
        </div>
        <div className="px-6 py-4 border-t-2 border-[var(--ink)] bg-[var(--cream)] flex items-center justify-end gap-2">
          <button onClick={onLater} className="btn-ghost text-sm" data-testid="mc-migration-welcome-later">Maybe later</button>
          <button onClick={onImport} className="btn-red text-sm flex items-center gap-1" data-testid="mc-migration-welcome-import">
            Import albums <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}


function MigrationImporter({ onClose, collectorId }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Iter Migration — celebratory completion screen shown ONCE after the
  // final pending album is handled this session.
  const [showCompletion, setShowCompletion] = useState(false);
  const [processedThisSession, setProcessedThisSession] = useState(0);
  // Per-item form state
  const [variantId, setVariantId] = useState("");
  const [modes, setModes] = useState([]);
  const [modeId, setModeId] = useState("");
  const [method, setMethod] = useState("applied_to_album");
  const [variantSearch, setVariantSearch] = useState("");
  const [manualVariants, setManualVariants] = useState([]);
  const [manualReleases, setManualReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [q, m] = await Promise.all([
          api.get("/collector-auth/my-collection/albums-to-import"),
          api.get("/catalog/sticker-modes"),
        ]);
        setItems(q.data.items || []);
        setModes(m.data.items || []);
        setModeId((m.data.items || []).find(x => x.code === "MIXED")?.id || (m.data.items || [])[0]?.id || "");
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not load albums to import.");
      } finally { setLoading(false); }
    })();
  }, []);

  const current = items[idx];

  // Reset form state whenever the active row changes.
  useEffect(() => {
    if (!current) return;
    const am = current.auto_match;
    if (am?.variant_id) {
      setVariantId(am.variant_id);
      setReleaseId(am.release_id);
      setManualVariants([]);
    } else if (am?.variants?.length) {
      setReleaseId(am.release_id);
      setManualVariants(am.variants);
      setVariantId(am.variants[0].id);
    } else {
      setVariantId("");
      setReleaseId("");
      setManualVariants([]);
    }
    setMethod(current.status === "sold" || current.status === "archived" ? "album_only" : "applied_to_album");
    setVariantSearch("");
    setManualReleases([]);
  }, [current?.id]);

  const searchCatalog = async (q) => {
    setVariantSearch(q);
    if (!q || q.length < 2) { setManualReleases([]); return; }
    try {
      // Simple client-side release search: pull all releases across Panini families.
      const fams = (await api.get("/catalog/manufacturers/panini/families")).data.items || [];
      const results = [];
      for (const f of fams.slice(0, 6)) {
        const rs = (await api.get(`/catalog/families/${f.slug}/releases`)).data.items || [];
        for (const r of rs) {
          const label = `${f.name} · ${r.name} ${r.host_country ? "· " + r.host_country : ""}`;
          if (label.toLowerCase().includes(q.toLowerCase())) results.push({...r, _family: f.name, _label: label});
          if (results.length >= 12) break;
        }
        if (results.length >= 12) break;
      }
      setManualReleases(results);
    } catch { /* noop */ }
  };

  const pickManualRelease = async (r) => {
    try {
      const vs = (await api.get(`/catalog/releases/${r.slug}/variants`)).data.items || [];
      setManualVariants(vs);
      setReleaseId(r.id);
      setVariantId(vs[0]?.id || "");
      setManualReleases([]);
      if (!vs.length) toast.info(`${r.name} has no album variants yet — request support below.`);
    } catch (e) {
      toast.error("Could not load variants for that release.");
    }
  };

  const doImport = async () => {
    if (!variantId) return toast.error("Please pick a catalog album first.");
    setBusy(true);
    try {
      await api.post(`/collector-auth/my-collection/albums-to-import/${current.id}/import`, {
        album_variant_id: variantId, sticker_mode_id: modeId, collection_method: method,
      });
      toast.success(`Imported "${current.album_name}".`);
      advance();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed.");
    } finally { setBusy(false); }
  };
  const doArchive = async () => {
    if (!current) return;
    if (!window.confirm(`Archive "${current.album_name}"? It won't appear in the import queue again but the record is preserved.`)) return;
    setBusy(true);
    try {
      await api.post(`/collector-auth/my-collection/albums-to-import/${current.id}/archive`);
      toast.success("Archived.");
      advance();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Archive failed.");
    } finally { setBusy(false); }
  };
  const doRequestSupport = async () => {
    if (!current) return;
    const notes = window.prompt("Add a note for the CollectorUp team (optional):", "") || "";
    setBusy(true);
    try {
      await api.post(`/collector-auth/my-collection/albums-to-import/${current.id}/request-support`, { notes });
      toast.success("Request submitted. We'll add support for this album.");
      advance();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Request failed.");
    } finally { setBusy(false); }
  };
  const advance = () => {
    setProcessedThisSession((n) => n + 1);
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    setIdx(0);
    if (next.length === 0) {
      // Iter Migration — Only show the celebratory completion once per
      // collector. Persist via localStorage so the screen never repeats.
      let alreadyShown = false;
      try { alreadyShown = localStorage.getItem(`mc-migration-done-${collectorId || "anon"}`) === "1"; }
      catch { /* noop */ }
      if (!alreadyShown && (processedThisSession + 1) > 0) {
        setShowCompletion(true);
        try { localStorage.setItem(`mc-migration-done-${collectorId || "anon"}`, "1"); }
        catch { /* noop */ }
      } else {
        onClose();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" role="dialog"
         data-testid="mc-migration-importer">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-yellow)] max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">Import album {items.length ? (idx + 1) : 0} of {items.length}</div>
            <div className="font-display text-2xl">Match your album to the catalog</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-white/70" data-testid="mc-migration-importer-close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
        ) : showCompletion ? (
          <div className="p-8 text-center" data-testid="mc-migration-completion">
            <div className="text-5xl mb-2" aria-hidden>🎉</div>
            <div className="font-display text-3xl md:text-4xl">Your Collection Upgrade is Complete!</div>
            <p className="text-sm text-[var(--smoke)] mt-3 max-w-md mx-auto">
              Your albums have been successfully imported into the new CollectorUp collection system.
            </p>
            <ul className="mt-6 max-w-md mx-auto text-left text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /> Track every sticker individually</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /> Share your public collection</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /> Manage album visibility</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /> Access future CollectorUp features</li>
            </ul>
            <button onClick={onClose} className="btn-red mt-8 flex items-center gap-2 mx-auto"
                    data-testid="mc-migration-completion-cta">
              Go to My Collection <ArrowRight size={14} />
            </button>
          </div>
        ) : !current ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={40} />
            <div className="font-display text-2xl">All albums imported</div>
            <div className="text-sm text-[var(--smoke)] mt-1">Nothing left in your import queue.</div>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="mc-migration-done">Done</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0 overflow-y-auto">
            {/* LEFT — Your previous album */}
            <div className="p-6 border-r-2 border-neutral-200 bg-[var(--cream)]/50">
              <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">Your previous album</div>
              <div className="font-display text-xl mt-1" data-testid="mc-migration-current-name">{current.album_name}</div>
              <dl className="text-xs mt-4 space-y-1.5">
                {current.manufacturer && <Row label="Manufacturer" v={current.manufacturer} />}
                {current.year && <Row label="Year" v={String(current.year)} />}
                {current.edition && <Row label="Edition" v={current.edition} />}
                {current.country_version && <Row label="Country / Version" v={current.country_version} />}
                {current.collection_type && <Row label="Type" v={current.collection_type} />}
                <Row label="Status" v={current.status} />
                <Row label="Created" v={fmtDate(current.created_at)} />
              </dl>
              {current.completion_percentage != null && (
                <div className="mt-5 rounded-lg border-2 border-[var(--ink)] bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">Legacy progress</div>
                  <div className="font-display text-4xl mt-1" data-testid="mc-migration-legacy-pct">{current.completion_percentage}%</div>
                  <div className="text-[11px] text-[var(--smoke)] mt-1">Previously recorded in CollectorUp.</div>
                  <div className="text-[11px] text-[var(--smoke)] mt-1">This album hasn&apos;t been converted into sticker inventory yet — after import, you&apos;ll track every sticker individually.</div>
                </div>
              )}
              {current.notes && (
                <div className="mt-3 text-xs text-[var(--smoke)] whitespace-pre-line">
                  <span className="font-bold text-[var(--ink)]">Notes: </span>{current.notes}
                </div>
              )}
            </div>

            {/* RIGHT — Catalog match */}
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">CollectorUp catalog</div>
              {current.auto_match?.variant_id ? (
                <div className="mt-1 flex items-center gap-2 text-emerald-800" data-testid="mc-migration-exact-match">
                  <CheckCircle2 size={16} /> <span className="text-sm font-bold">Exact match found</span>
                </div>
              ) : current.auto_match ? (
                <div className="mt-1 flex items-center gap-2 text-amber-800">
                  <HelpCircle size={16} /> <span className="text-sm font-bold">Choose the exact variant</span>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2 text-neutral-700">
                  <HelpCircle size={16} /> <span className="text-sm font-bold">Search the catalog</span>
                </div>
              )}

              {current.auto_match && (
                <div className="mt-3 rounded-lg border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-3">
                  <div className="font-bold text-sm">{current.auto_match.release_official_title || current.auto_match.release_name}</div>
                  <div className="text-[11px] text-[var(--smoke)]">
                    {current.auto_match.release_year}{current.auto_match.release_host_country ? ` · ${current.auto_match.release_host_country}` : ""}
                  </div>
                </div>
              )}

              {/* Variant picker — either single-variant auto or picker list */}
              {manualVariants.length > 0 && (
                <label className="block mt-3">
                  <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">Variant</span>
                  <select value={variantId} onChange={(e) => setVariantId(e.target.value)}
                          className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white text-sm"
                          data-testid="mc-migration-variant">
                    {manualVariants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </label>
              )}

              {/* Manual search — expanded when no auto-match OR user wants override */}
              {!current.auto_match && (
                <label className="block mt-3">
                  <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">Find in catalog</span>
                  <div className="mt-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
                    <input value={variantSearch} onChange={(e) => searchCatalog(e.target.value)}
                            placeholder="e.g. World Cup 2018, Euro 2020…"
                            className="w-full border-2 border-[var(--ink)] rounded-lg pl-9 pr-3 py-2 text-sm bg-white"
                            data-testid="mc-migration-search" />
                  </div>
                  {manualReleases.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border-2 border-[var(--ink)] bg-white divide-y">
                      {manualReleases.map(r => (
                        <button key={r.id} type="button" onClick={() => pickManualRelease(r)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--panini-yellow-soft)]">
                          {r._label}
                        </button>
                      ))}
                    </div>
                  )}
                </label>
              )}

              <SelectField label="Sticker Mode" value={modeId} onChange={setModeId}
                            options={modes.map(m => [m.id, m.name])}
                            testid="mc-migration-mode" />
              <SelectField label="Collection Method" value={method} onChange={setMethod}
                            options={[["applied_to_album","Applied to Album"],
                                      ["stored_in_binder","Stored in Binder"],
                                      ["album_only","Album Only — Kept Unstickered"]]}
                            testid="mc-migration-method" />
            </div>
          </div>
        )}

        {current && !loading && !showCompletion && (
          <div className="px-6 py-4 border-t-2 border-[var(--ink)] bg-[var(--cream)] flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button onClick={doArchive} disabled={busy} className="btn-ghost text-xs" data-testid="mc-migration-archive">
                <Archive size={12} /> Archive album
              </button>
              <button onClick={doRequestSupport} disabled={busy} className="btn-ghost text-xs" data-testid="mc-migration-request">
                Request album support
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} disabled={busy} className="btn-ghost text-xs" data-testid="mc-migration-not-now">Not now</button>
              <button onClick={doImport} disabled={busy || !variantId} className="btn-red text-sm flex items-center gap-1"
                      data-testid="mc-migration-import">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Import
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function Row({ label, v }) {
  return (
    <div className="flex gap-3">
      <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)] w-28 shrink-0 pt-0.5">{label}</dt>
      <dd className="text-xs text-[var(--ink)] font-bold">{v}</dd>
    </div>
  );
}
